/*
  Smooth carousel
  ---------------
  Native horizontal scrolling with a slow continuous drift, used by the homepage
  book and merch rows. Replaces the previous Splide setup, which intercepted
  trackpad gestures and jumped a whole page at a time.

  Why native scrolling: the browser gives us real trackpad and touch momentum for
  free. Anything transform-based has to reimplement that, and never quite matches.

  Markup contract:
    <div data-carousel data-carousel-speed="20">
      <ul data-carousel-track> <li>…</li> </ul>
      <button data-carousel-prev>  <button data-carousel-next>   (both optional)
    </div>

  The drift pauses on hover, focus, drag, wheel, and touch, then resumes once
  things have been still for a moment. It never runs off-screen, in a hidden tab,
  or when the visitor prefers reduced motion.
*/
(function () {
  'use strict';

  var DEFAULT_SPEED = 20; // pixels per second
  /* Moving the mouse away is not really an interaction, so the drift comes back
     almost at once. After a real scroll or drag it waits longer, so it is not
     tugging against someone still reading where they landed. */
  var RESUME_AFTER_HOVER = 400;
  var RESUME_AFTER_INPUT = 1400;
  var DRAG_THRESHOLD = 5; // px of movement before a drag suppresses the click

  function setup(root) {
    if (root.dataset.carouselReady === 'true') return;

    var track = root.querySelector('[data-carousel-track]');
    if (!track || !track.children.length) return;

    root.dataset.carouselReady = 'true';

    var speed = parseFloat(root.dataset.carouselSpeed) || DEFAULT_SPEED;
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    var paused = false;
    var onScreen = true;
    var dragging = false;
    var cloned = false;
    var resumeTimer = null;
    var lastFrame = null;
    var position = 0;
    var lastWritten = 0;
    var loopDistance = 0;

    /* A seamless loop needs copies to scroll into at the seam, but only enough to
       cover one screen. Cloning the whole row would double the cards and their
       images for no visible gain. */
    function buildLoop() {
      if (cloned || track.scrollWidth <= track.clientWidth + 4) return;

      var originals = Array.prototype.slice.call(track.children);
      var needed = track.clientWidth + 200;
      var covered = 0;
      var index = 0;

      while (covered < needed && index < originals.length) {
        var copy = originals[index].cloneNode(true);
        copy.setAttribute('aria-hidden', 'true');
        copy.dataset.carouselClone = 'true';
        Array.prototype.forEach.call(copy.querySelectorAll('a, button, input, [tabindex]'), function (el) {
          el.setAttribute('tabindex', '-1');
        });
        track.appendChild(copy);
        covered += copy.getBoundingClientRect().width;
        index++;
      }

      /* Measure the seam rather than computing it: offsets already account for
         gaps and padding, which arithmetic on widths would get subtly wrong. */
      var firstClone = track.querySelector('[data-carousel-clone]');
      loopDistance = firstClone ? firstClone.offsetLeft - originals[0].offsetLeft : 0;
      cloned = loopDistance > 0;
    }

    function clearLoop() {
      Array.prototype.forEach.call(track.querySelectorAll('[data-carousel-clone]'), function (el) {
        el.remove();
      });
      cloned = false;
      loopDistance = 0;
    }

    function loopWidth() {
      return cloned ? loopDistance : 0;
    }

    /* Keep the scroll position inside the first copy so the seam never shows. */
    function wrap() {
      var half = loopWidth();
      if (!half) return;
      if (position >= half) {
        position -= half;
        track.scrollLeft = position;
        lastWritten = track.scrollLeft;
      } else if (position < 0) {
        position += half;
        track.scrollLeft = position;
        lastWritten = track.scrollLeft;
      }
    }

    function pause() {
      paused = true;
      if (resumeTimer) clearTimeout(resumeTimer);
    }

    function scheduleResume(delay) {
      if (resumeTimer) clearTimeout(resumeTimer);
      resumeTimer = setTimeout(function () {
        position = track.scrollLeft;
        paused = false;
      }, delay || RESUME_AFTER_INPUT);
    }

    function frame(now) {
      if (lastFrame === null) lastFrame = now;
      var elapsed = Math.min(now - lastFrame, 100);
      lastFrame = now;

      /* If anything else moved the row (keyboard, scroll restoration, a link
         jump), adopt its position instead of yanking it back. */
      if (Math.abs(track.scrollLeft - lastWritten) > 1.5) {
        position = track.scrollLeft;
      }

      if (!paused && onScreen && !reduceMotion.matches && cloned) {
        /* Track position as a float and write it every frame. Incrementing
           scrollLeft directly stalls in browsers that round it to integers. */
        position += (speed * elapsed) / 1000;
        track.scrollLeft = position;
        lastWritten = track.scrollLeft;
        wrap();
      }

      requestAnimationFrame(frame);
    }

    /* Hover: reading a card should not make it slide away. Bound to the cards
       themselves, not to the carousel block, which also spans the arrows row and
       the empty space either side of it: hovering there used to stop the drift
       from what reads as nowhere near the books.

       pointerover/pointerout rather than enter/leave because these bubble, so one
       pair of listeners covers every card, including ones cloned in later. */
    function cardUnder(node) {
      return node && node.closest ? node.closest('[data-carousel-track] > *') : null;
    }

    track.addEventListener('pointerover', function (event) {
      if (event.pointerType !== 'mouse') return;
      if (cardUnder(event.target)) pause();
    });

    track.addEventListener('pointerout', function (event) {
      if (event.pointerType !== 'mouse') return;
      /* Moving between the cover and the caption inside one card is not leaving
         it, and neither is crossing into the next card. */
      if (cardUnder(event.target) && !cardUnder(event.relatedTarget) && !dragging) {
        scheduleResume(RESUME_AFTER_HOVER);
      }
    });

    root.addEventListener('focusin', pause);
    root.addEventListener('focusout', function () {
      scheduleResume(RESUME_AFTER_HOVER);
    });

    /* Trackpad and wheel: native scrolling handles the movement, we just yield. */
    track.addEventListener(
      'wheel',
      function () {
        pause();
        scheduleResume();
      },
      { passive: true }
    );

    track.addEventListener(
      'touchstart',
      function () {
        pause();
      },
      { passive: true }
    );
    track.addEventListener(
      'touchend',
      function () {
        scheduleResume();
      },
      { passive: true }
    );

    /* Click-and-drag for mice. Touch is left to the browser, which does it better.

       Two things here are deliberate and were both bugs first:
       capture is taken only once a drag is real, because capturing on every
       pointerdown retargets the follow-up click to the track and a plain click
       on a card then goes nowhere; and a finished drag suppresses clicks for a
       moment via a flag rather than a one-shot listener, which used to sit armed
       and eat an unrelated click later on. */
    var dragStartX = 0;
    var dragStartScroll = 0;
    var dragDistance = 0;
    var pendingPointerId = null;
    var suppressClickUntil = 0;

    /* Press on a cover and move, and the browser starts its own image-and-link
       drag: a ghost thumbnail follows the cursor and the pointer stream is cut
       off with pointercancel, so the row stops after a few pixels. Nothing here
       is meant to be dragged out of the page, so refuse it outright. Listening
       on the track covers the clones too, since dragstart bubbles. */
    track.addEventListener('dragstart', function (event) {
      event.preventDefault();
    });

    track.addEventListener('pointerdown', function (event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      pendingPointerId = event.pointerId;
      dragDistance = 0;
      dragStartX = event.clientX;
      dragStartScroll = track.scrollLeft;
      pause();
    });

    track.addEventListener('pointermove', function (event) {
      if (pendingPointerId === null || event.pointerId !== pendingPointerId) return;

      var moved = event.clientX - dragStartX;
      dragDistance = Math.abs(moved);

      if (!dragging) {
        if (dragDistance <= DRAG_THRESHOLD) return; // still just a click
        dragging = true;
        track.classList.add('is-dragging');
        try {
          track.setPointerCapture(event.pointerId);
        } catch (e) {
          /* capture is a nicety; dragging still works without it */
        }
      }

      track.scrollLeft = dragStartScroll - moved;
      position = track.scrollLeft;
      lastWritten = track.scrollLeft;
      wrap();
    });

    function endDrag(event) {
      if (pendingPointerId === null) return;

      if (dragging) {
        dragging = false;
        track.classList.remove('is-dragging');
        try {
          if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
        } catch (e) {
          /* nothing to release */
        }
        /* Let go after dragging and you should not also open the card underneath. */
        suppressClickUntil = performance.now() + 250;
        position = track.scrollLeft;
      }

      pendingPointerId = null;
      scheduleResume();
    }

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    track.addEventListener(
      'click',
      function (event) {
        if (performance.now() < suppressClickUntil) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );

    /* Arrows move by one card, using the browser's own smooth scrolling. */
    function step(direction) {
      var first = track.querySelector(':scope > *');
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      var distance = first ? first.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
      pause();
      track.scrollBy({ left: direction * distance, behavior: 'smooth' });
      setTimeout(function () {
        position = track.scrollLeft;
      }, 400);
      scheduleResume();
    }

    /* Controls usually sit inside the carousel, but the merch row keeps them in a
       sibling column, so fall back to the enclosing section. */
    var scope = root.closest('[id^="shopify-section-"]') || document;
    var prev = root.querySelector('[data-carousel-prev]') || scope.querySelector('[data-carousel-prev]');
    var next = root.querySelector('[data-carousel-next]') || scope.querySelector('[data-carousel-next]');
    if (prev) prev.addEventListener('click', function () { step(-1); });
    if (next) next.addEventListener('click', function () { step(1); });

    /* Do not animate a row nobody is looking at. */
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        onScreen = entries[0].isIntersecting;
      }, { threshold: 0 }).observe(root);
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        pause();
      } else {
        /* Coming back to the tab is not an interaction either. */
        scheduleResume(RESUME_AFTER_HOVER);
      }
    });

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        clearLoop();
        position = 0;
        track.scrollLeft = 0;
        buildLoop();
      }, 250);
    });

    /* A row that is display:none at setup measures zero wide, so there is nothing
       to loop yet and buildLoop bails. The book club page keeps one row per year
       in the DOM and shows one at a time, so watch for the row getting a real
       width later and build the loop then. */
    var lastWidth = track.clientWidth;
    if ('ResizeObserver' in window) {
      new ResizeObserver(function () {
        var width = track.clientWidth;
        if (width === lastWidth) return;
        lastWidth = width;
        /* Hidden again: keep the clones, the row comes back the same size. */
        if (!width || cloned) return;
        position = 0;
        track.scrollLeft = 0;
        lastWritten = 0;
        buildLoop();
      }).observe(track);
    }

    buildLoop();
    requestAnimationFrame(frame);
  }

  function initAll(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-carousel]'), setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  /* Theme editor re-renders sections in place. */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
